import type {
  Fax,
  FaxAnalysis,
  FaxCostEstimateRequest,
  FaxCostEstimateResponse,
  FaxListFilters,
  FaxListItem,
  FaxPatientLinkResult,
  FaxSuggestionKind,
  FaxUploadResponse,
  LinkFaxPatientRequest,
  SendFaxRequest,
} from "@/types/fax";
import type { CursorPage } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export async function fetchFaxes(
  filters: FaxListFilters = {},
): Promise<CursorPage<FaxListItem> | null> {
  const params = new URLSearchParams();
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.perPage) params.set("perPage", String(filters.perPage));
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.status) params.set("status", filters.status);
  if (filters.faxContactId) params.set("faxContactId", filters.faxContactId);
  if (filters.phoneNumberId) params.set("phoneNumberId", filters.phoneNumberId);
  if (filters.beforeDate) params.set("beforeDate", filters.beforeDate);
  if (filters.afterDate) params.set("afterDate", filters.afterDate);
  const qs = params.toString();
  const endpoint = qs ? `faxes?${qs}` : "faxes";
  return await requestData<CursorPage<FaxListItem>>(endpoint, "get");
}

// Pydantic serializes Decimal as a string over JSON, so normalize cost to a
// number at the API boundary — keeps the Fax type honest (`number | null`) and
// lets downstream callers do arithmetic without per-call parseFloat. Returns a
// new object on rewrite so React Query never sees in-place cache mutation.
function normalizeFax<T extends Fax | null>(fax: T): T {
  if (fax == null) return fax;
  const raw: unknown = fax.cost;
  if (typeof raw === "string") {
    const parsed = Number.parseFloat(raw);
    return { ...fax, cost: Number.isFinite(parsed) ? parsed : null } as T;
  }
  return fax;
}

export async function getFax(id: string): Promise<Fax | null> {
  return normalizeFax(await requestData<Fax>(`faxes/${id}`, "get"));
}

export async function sendFax(data: SendFaxRequest): Promise<Fax | null> {
  return normalizeFax(
    await requestData<Fax>("faxes", "post", { json: data }, false),
  );
}

export async function estimateFaxCost(
  data: FaxCostEstimateRequest,
  signal?: AbortSignal,
): Promise<FaxCostEstimateResponse | null> {
  return await requestData<FaxCostEstimateResponse>(
    "faxes/estimate-cost",
    "post",
    { json: data, signal },
    false,
  );
}

export async function uploadFaxAttachment(
  file: File,
  signal?: AbortSignal,
): Promise<FaxUploadResponse | null> {
  const formData = new FormData();
  formData.append("file", file);
  return await requestData<FaxUploadResponse>(
    "faxes/upload",
    "post",
    { body: formData, signal },
    false,
    120000, // raises ky's 20 s default: a 50 MB scan can take longer to upload
  );
}

export async function markFaxRead(faxId: string): Promise<FaxListItem | null> {
  return await requestData<FaxListItem>(`faxes/${faxId}/mark-read`, "post");
}

// Both link/unlink return the updated fax plus the affected chart document
// (FaxPatientLinkResult). Normalize the nested fax's Decimal cost the same way
// the other fax endpoints do, leaving the document untouched.
function normalizeLinkResult<T extends FaxPatientLinkResult | null>(
  result: T,
): T {
  if (result == null) return result;
  return { ...result, fax: normalizeFax(result.fax) } as T;
}

export async function linkFaxToPatient(
  faxId: string,
  data: LinkFaxPatientRequest,
): Promise<FaxPatientLinkResult | null> {
  return normalizeLinkResult(
    await requestData<FaxPatientLinkResult>(
      `faxes/${faxId}/link-patient`,
      "post",
      { json: data },
      false,
    ),
  );
}

export async function unlinkFaxPatient(
  faxId: string,
): Promise<FaxPatientLinkResult | null> {
  return normalizeLinkResult(
    await requestData<FaxPatientLinkResult>(
      `faxes/${faxId}/unlink-patient`,
      "post",
      undefined,
      false,
    ),
  );
}

/** (Re)run the AI analysis for an inbound fax — serves "Retry analysis" and
 *  pre-feature faxes that never got a run. Returns the reset PENDING row. */
export async function analyzeFax(faxId: string): Promise<FaxAnalysis | null> {
  return await requestData<FaxAnalysis>(
    `faxes/${faxId}/analyze`,
    "post",
    undefined,
    false,
  );
}

/** Generate a clinical note from the fax PDF (slow — the server runs the
 *  template fill synchronously, ~10-30s). Requires the fax to be linked to a
 *  patient. Returns the updated analysis with `createdNoteId` set; a repeat
 *  call returns the existing live note (idempotent). */
export async function createNoteFromFax(
  faxId: string,
  noteTemplateId?: string,
): Promise<FaxAnalysis | null> {
  return await requestData<FaxAnalysis>(
    `faxes/${faxId}/create-note`,
    "post",
    { json: { noteTemplateId: noteTemplateId ?? null } },
    false,
    // The server runs the template fill synchronously (~10-30s). The default
    // 20s ky timeout would abort mid-generation, showing an error while the
    // server still commits the note — give it a comfortable ceiling.
    60_000,
  );
}

/** Hide one suggestion card (idempotent server-side). Returns the updated analysis. */
export async function dismissFaxSuggestion(
  faxId: string,
  kind: FaxSuggestionKind,
): Promise<FaxAnalysis | null> {
  return await requestData<FaxAnalysis>(
    `faxes/${faxId}/analysis/dismiss`,
    "post",
    { json: { kind } },
    false,
  );
}

export async function fetchFaxUnreadCount(): Promise<number | null> {
  const result = await requestData<{ count: number }>(
    "faxes/unread-count",
    "get",
  );
  return result?.count ?? null;
}
